import { Component, Input, OnInit, ViewChild, ElementRef } from "@angular/core";
import { BsModalRef } from "ngx-bootstrap/modal";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Media } from "src/app/core/services/interface";

@Component({
  selector: "app-media-player",
  templateUrl: "./media-player.component.html",
  styleUrls: ["./media-player.component.scss"],
})
export class MediaPlayerComponent implements OnInit {
  @Input() mediaUrl: string;
  @Input() mediaType: string; // 'image', 'video', 'audio'
  @Input() mediaList: Media[] = []; // List of all media items
  @Input() currentIndex: number = 0; // Current media index
  @ViewChild("audioPlayer", { static: false }) audioPlayer: ElementRef;
  @ViewChild("videoPlayer", { static: false }) videoPlayer: ElementRef;

  currentMedia: Media;

  constructor(
    public modalRef: BsModalRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    if (this.mediaList && this.mediaList.length > 0) {
      this.currentMedia = this.mediaList[this.currentIndex];
      this.mediaUrl = this.currentMedia.url;
    }
    this.detectMediaType();
  }

  detectMediaType(): void {
    if (!this.mediaType && this.mediaUrl) {
      const url = this.mediaUrl.toLowerCase();
      if (
        url.match(
          /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i
        ) ||
        url.includes("image")
      ) {
        this.mediaType = "image";
      } else if (
        url.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i) ||
        url.includes("video")
      ) {
        this.mediaType = "video";
      } else if (
        url.match(/\.(mp3|wav|ogg|flac|aac|m4a)$/i) ||
        url.includes("audio")
      ) {
        this.mediaType = "audio";
      } else if (
        url.includes("youtube.com") ||
        url.includes("youtu.be")
      ) {
        this.mediaType = "youtube";
      } else if (
        url.includes("vimeo.com")
      ) {
        this.mediaType = "vimeo";
      } else {
        this.mediaType = "image"; // default
      }
    }
  }

  getYoutubeEmbedUrl(): SafeResourceUrl {
    let videoId = "";
    if (this.mediaUrl.includes("youtu.be/")) {
      videoId = this.mediaUrl.split("youtu.be/")[1].split("?")[0];
    } else if (this.mediaUrl.includes("youtube.com")) {
      videoId = this.mediaUrl.split("v=")[1]?.split("&")[0];
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${videoId}`
    );
  }

  getVimeoEmbedUrl(): SafeResourceUrl {
    let videoId = "";
    if (this.mediaUrl.includes("vimeo.com/")) {
      videoId = this.mediaUrl.split("vimeo.com/")[1].split("?")[0];
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://player.vimeo.com/video/${videoId}`
    );
  }

  close(): void {
    this.modalRef.hide();
  }

  downloadMedia(): void {
    const link = document.createElement("a");
    link.href = this.mediaUrl;
    link.download = `media.${this.mediaType}`;
    link.click();
  }

  playPause(): void {
    if (this.mediaType === "audio" && this.audioPlayer) {
      const audio = this.audioPlayer.nativeElement;
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
    }
  }

  nextMedia(): void {
    if (this.mediaList && this.currentIndex < this.mediaList.length - 1) {
      this.currentIndex++;
      this.currentMedia = this.mediaList[this.currentIndex];
      this.mediaUrl = this.currentMedia.url;
      this.detectMediaType();
    }
  }

  previousMedia(): void {
    if (this.mediaList && this.currentIndex > 0) {
      this.currentIndex--;
      this.currentMedia = this.mediaList[this.currentIndex];
      this.mediaUrl = this.currentMedia.url;
      this.detectMediaType();
    }
  }

  addToConsole(): void {
    const mediaData = {
      url: this.mediaUrl,
      type: this.mediaType,
      index: this.currentIndex,
      total: this.mediaList.length,
      media: this.currentMedia,
    };
    console.log("Selected Media:", mediaData);
    console.table(mediaData);
  }

  canGoNext(): boolean {
    return this.mediaList && this.currentIndex < this.mediaList.length - 1;
  }

  canGoPrevious(): boolean {
    return this.mediaList && this.currentIndex > 0;
  }
}
